import React, { useState, useRef, useEffect } from 'react';
import { useWorldStore } from '../state/worldStore';
import { sendSay } from '../services/worldSocket';
import { edgeInset } from '../config';

// ChatBar — proximity chat: tap 💬 to open, type, Enter to send (Esc/✕ closes).
// What you say appears as a speech bubble over your figure to peers in your area
// (rendered by <Peers/>); their lines show here + over their heads.
// Bottom-RIGHT, above the music/now-playing cluster — kept clear of the centre
// movement controls (WASD / joystick) so it never overlays them.
const wrap = { position: 'fixed', right: edgeInset(12, 'right'), bottom: 'calc(max(16px, env(safe-area-inset-bottom)) + 108px)', zIndex: 46, pointerEvents: 'auto', fontFamily: 'system-ui, sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 };
const toggle = { width: 42, height: 42, borderRadius: '50%', border: '1px solid rgba(255,178,94,0.4)', background: 'rgba(16,10,6,0.72)', color: '#ffd27d', fontSize: 18, cursor: 'pointer', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' };
const logBox = { width: 'min(320px, 82vw)', display: 'flex', flexDirection: 'column', gap: 3, maxHeight: '30vh', overflow: 'hidden', pointerEvents: 'none' };
const line = { background: 'rgba(10,6,3,0.6)', color: '#ffe9c4', font: '13px system-ui', padding: '4px 9px', borderRadius: 10, alignSelf: 'flex-start', maxWidth: '100%' };
const row = { display: 'flex', gap: 6, width: 'min(320px, 82vw)' };
const inputStyle = { flex: 1, padding: '9px 12px', borderRadius: 999, border: '1px solid rgba(255,178,94,0.4)', background: 'rgba(16,10,6,0.9)', color: '#fff', fontSize: 15, outline: 'none', minWidth: 0 };
const sendBtn = { padding: '0 14px', borderRadius: 999, border: 'none', background: '#ffb25e', color: '#1c1206', font: '700 14px system-ui', cursor: 'pointer' };
const closeBtn = { width: 38, borderRadius: 999, border: '1px solid rgba(255,178,94,0.35)', background: 'transparent', color: '#ffd27d', cursor: 'pointer', fontSize: 15 };

export default function ChatBar() {
  const entered = useWorldStore((s) => s.entered);
  const chat = useWorldStore((s) => s.chat);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  if (!entered) return null;

  const send = () => { const t = text.trim(); if (t) { sendSay(t); setText(''); } };

  if (!open) {
    return (
      <div style={wrap}>
        <button style={toggle} onClick={() => setOpen(true)} title="Say something to people nearby">💬</button>
      </div>
    );
  }

  const recent = chat.slice(-4);
  return (
    <div style={wrap}>
      {recent.length > 0 && (
        <div style={logBox}>
          {recent.map((m, i) => (
            <div key={`${m.ts}-${i}`} style={line}>
              <b style={{ color: m.me ? '#ffd27d' : '#9dff7d' }}>{m.handle}</b> {m.text}
            </div>
          ))}
        </div>
      )}
      <div style={row}>
        <input
          ref={inputRef} value={text} maxLength={200} placeholder="Say something…"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); else if (e.key === 'Escape') setOpen(false); }}
          style={inputStyle}
        />
        <button style={sendBtn} onClick={send}>Send</button>
        <button style={closeBtn} onClick={() => setOpen(false)}>✕</button>
      </div>
    </div>
  );
}
